import { supabase } from '@/integrations/supabase/client';

export interface DiplomaLayoutElement {
  enabled: boolean;
  x: number;        // % width (0-100)
  y: number;        // % height (0-100)
  fontSize: number; // pt
  color: string;    // hex (#rrggbb)
  align: 'left' | 'center' | 'right';
}

export interface DiplomaLayoutQR {
  enabled: boolean;
  x: number;     // % width
  y: number;     // % height
  size: number;  // mm
}

export interface DiplomaLayout {
  participantName: DiplomaLayoutElement;
  diplomaName: DiplomaLayoutElement;
  date: DiplomaLayoutElement;
  certificateId: DiplomaLayoutElement;
  entities: DiplomaLayoutElement;
  publicUrl: DiplomaLayoutElement;
  qr: DiplomaLayoutQR;
}

export const DEFAULT_DIPLOMA_LAYOUT: DiplomaLayout = {
  participantName: { enabled: true, x: 50, y: 50, fontSize: 32, color: '#1e1e1e', align: 'center' },
  diplomaName:     { enabled: false, x: 50, y: 60, fontSize: 16, color: '#3c3c3c', align: 'center' },
  date:            { enabled: true, x: 5,  y: 92, fontSize: 9,  color: '#3c3c3c', align: 'left' },
  certificateId:   { enabled: true, x: 5,  y: 95, fontSize: 9,  color: '#3c3c3c', align: 'left' },
  entities:        { enabled: true, x: 5,  y: 97, fontSize: 9,  color: '#3c3c3c', align: 'left' },
  publicUrl:       { enabled: true, x: 50, y: 96, fontSize: 8,  color: '#505050', align: 'center' },
  qr:              { enabled: true, x: 12, y: 83, size: 25 },
};

export interface DiplomaConfig {
  name: string;
  description: string;
  image_url: string | null;
  skills: any[];
  entities: string;
  additional_info: string;
  additional_url: string;
  priority: number;
  require_content: boolean;
  min_content_percent: number;
  require_deliverables: boolean;
  min_deliverables_percent: number;
  require_project: boolean;
  require_project_approved: boolean;
  require_project_answered: boolean;
  require_lab: boolean;
  layout: DiplomaLayout;
}

// Regla documentada de los diplomas: A = TODO (contenido + entregables + proyecto
// + aprobado + preguntas + lab); B = igual pero SIN aprobación. A tiene mayor
// prioridad para ganar el desempate cuando un participante cumple ambos.
// Un certificado NUEVO nace con estos requisitos por defecto (antes nacía con una
// config revuelta según priority, lo que creó certificados mal configurados).
export const emptyDiploma = (variant: 'A' | 'B' = 'A', defaults?: Partial<DiplomaConfig>): DiplomaConfig => ({
  name: '',
  description: '',
  image_url: null,
  skills: [],
  entities: '',
  additional_info: '',
  additional_url: '',
  priority: variant === 'A' ? 2 : 1,
  require_content: true,
  min_content_percent: 100,
  require_deliverables: true,
  min_deliverables_percent: 100,
  require_project: true,
  require_project_approved: variant === 'A',
  require_project_answered: true,
  require_lab: true,
  layout: DEFAULT_DIPLOMA_LAYOUT,
  ...defaults,
});

/** Merge a partial layout (from DB) with defaults so missing keys never break the editor/PDF. */
export function normalizeDiplomaLayout(input: any): DiplomaLayout {
  const d = DEFAULT_DIPLOMA_LAYOUT;
  if (!input || typeof input !== 'object') return d;
  const merge = (k: keyof DiplomaLayout) => ({ ...(d[k] as any), ...(input[k] as any) });
  return {
    participantName: merge('participantName'),
    diplomaName:     merge('diplomaName'),
    date:            merge('date'),
    certificateId:   merge('certificateId'),
    entities:        merge('entities'),
    publicUrl:       merge('publicUrl'),
    qr:              { ...d.qr, ...(input.qr || {}) },
  };
}

export function useCertificateConfig() {
  const uploadDiplomaImage = async (file: File): Promise<string> => {
    const ext = file.name.split('.').pop() || 'png';
    const path = `diplomas/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage
      .from('certificate-images')
      .upload(path, file, { contentType: file.type, upsert: false });
    if (error) throw error;
    const { data } = supabase.storage.from('certificate-images').getPublicUrl(path);
    return data.publicUrl;
  };

  return { uploadDiplomaImage };
}
